/**
 * Responsible for creating an accordion based on elements existing in the DOM
 * @module modules/accordion
 */

import $ from 'jquery';
import './horizontal-scroll';

import registerJQueryPlugin from '../lib/register-jquery-plugin';

export const PLUGIN_NAME = 'accordion';
export const CLICK_EVENT = 'click';
export const AUTO_VALUE = 'auto';
export const BLOCK_VALUE = 'block';
export const CHECKED_ATTR = 'checked';
export const FORCE_OPEN_CLASS = '.accordion__item--force';
export const DEFAULTS = {
  accItem: '.accordion__item',
  accContent: '.accordion__content',
  accActive: 'accordion__item--active',
  accRadio: 'js-accordion-radio',
  accLabel: '.accordion__label',
  accSpeed: 200,
  accToggle: false,
  accDataToggleAttr: 'toggle',
  accQueryOpen: false,
  accDataQueryAttr: 'queryString',
  onComplete: () => { },
  onLoad: () => { },
};

/**
 * Merges together all the options and the various conditions in which they
 * can be set.
 * @private
 * @param  {jQuery} $el - The container element
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @return {Object} Immutable version of the options
 */
const mergeOptions = ($el, options = {}) => {
  let opts = $.extend({}, DEFAULTS, options);
  const { accDataToggleAttr, accDataQueryAttr } = opts;

  if ($el.data(accDataToggleAttr) === true) {
    opts = $.extend({}, opts, { accToggle: true });
  }

  if ($el.data(accDataQueryAttr) === true) {
    opts = $.extend({}, opts, { accQueryOpen: true });
  }

  return opts;
};

/**
 * Handles any sort of defaults when the accordion is called upon.
 * @private
 * @param  {jQuery} $el - The container element
 * @param  {Object} options - Any options provided upon initialization
 * @param  {string} options.accActive - Selector representing the active
 * className
 * @param  {string} options.accContent - Selector representing the content for
 * the tab
 */
const applyDefaultsToActive = ($el, { accActive, accContent, accLabel }) => {
  $(`.${accActive}`, $el)
    .find(accContent)
    .css({
      display: BLOCK_VALUE,
      height: AUTO_VALUE,
    })
    .find(accLabel)
    .attr('aria-expanded', 'true');
};

/**
 * Handles any sort of defaults when the accordion is using radio buttons.
 * @private
 * @param  {jQuery} $el - The container element
 * @param  {Object} options - Any options provided upon initialization
 * @param  {string} options.accActive - Selector representing the active
 * className
 * @param  {string} options.accContent - Selector representing the content for
 * the tab
 * @param  {string} options.accItem - Selector representing the tab
 * @param  {string} options.accRadio - Selector representing a radio button
 */
const applyDefaultsToRadio = ($el, { accActive, accContent, accItem, accRadio }) => {
  $(`.${accRadio}:${CHECKED_ATTR}`, $el)
    .parents(accItem)
    .addClass(accActive)
    .find(accContent)
    .css({ height: AUTO_VALUE });
};

const applyDefaultsToForce = ($el, { accContent }) => {
  const $forceContents = $el.find(FORCE_OPEN_CLASS).find(accContent);

  $forceContents
    .css({
      display: BLOCK_VALUE,
      height: AUTO_VALUE,
    });
};

/**
 * Closes the active tab
 * @private
 * @param  {jQuery} $parent - The parent containing the entire tab.
 * @param  {Object} options - Any options provided upon initialization
 * @param  {string} options.accLabel - Selector representing the label className
 * @param  {string} options.accActive - Selector representing the active
 * className
 * @param  {string} options.accContent - Selector representing the content for
 * the tab
 * @param  {string} options.accItem - Selector representing the tab
 * @param  {number} options.accSpeed - Speed in ms for the closing animation
 */
const closeActiveTab = ($el, { accLabel, accActive, accContent, accItem, accSpeed }) => {
  $(`.${accActive}`, $el).find(accContent).stop().animate(
    { height: 0 },
    accSpeed,
    function callback() {
      $(this).removeAttr('style');
    },
  );
  $el.find(accLabel).attr('aria-expanded', false);
  $el.find(accItem).removeClass(accActive);
};

/**
 * Lazy loads images inside the accordion content when opened.
 * @private
 * @param {Object} content - The content of the interacted label
 */
const lazyloadImages = (content) => {
  // If images need to be lazy loaded - follow example markup below to load them
  // <img class="js-lazy-image"
  //      src="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
  //      data-lazy="image/url"
  //      alt=""
  // />
  const lazyImages = content.find('.js-lazy-image').not('.loaded');
  if (lazyImages) {
    $(lazyImages).each(function eachLazyImage() {
      const $lazyImage = $(this);
      if (!$lazyImage.hasClass('loaded')) {
        $lazyImage.attr('src', $lazyImage.attr('data-lazy')).addClass('loaded');
      }
    });
  }
};

/**
 * Initialize a Horizonal Scroll module in the accordion content when opened.
 * @private
 * @param {Object} content - The content of the interacted label
 */
const lazyloadHorizontalScroll = (content) => {
  const horizontalScroll = content.find('.js-horizontal-scroll-accordion');
  if (horizontalScroll) {
    horizontalScroll.horizontalScroll();
  }
};

/**
 * Opens a tab and provides any DOM manipulation and styles used to show a tab.
 * @private
 * @param {jQuery} $el - Containing the elements for the accordion
 * @param {jQuery} $parent - The parent of the interacted label
 * @param  {Object} options - Any options provided upon initialization
 * @param  {string} options.accLabel - Selector representing the label className
 * @param  {string} options.accActive - Selector representing the active
 * className
 * @param  {string} options.accContent - Selector representing the content for
 * the tab
 * @param  {string} options.accRadio - Selector representing a radio button
 * @param  {number} options.accSpeed - Speed in ms for the closing animation
 * @param  {Function} options.onComplete - Called on when animation has
 * completed
 */
const openTab = ($el, $parent, { accLabel, accActive, accContent, accRadio,
  accSpeed, onComplete }) => {
  const content = $parent.find(accContent);
  content.css({ height: AUTO_VALUE, display: BLOCK_VALUE });

  const contentHeight = content.outerHeight(true);

  lazyloadImages(content);
  lazyloadHorizontalScroll(content);

  content.css({ height: 0 }).stop().animate(
    { height: contentHeight },
    accSpeed,
    () => {
      content.css({ height: AUTO_VALUE });

      if ($el.attr('data-resize-colorbox') === 'true') {
        if ($.colorbox && typeof $.colorbox.resize === 'function') {
          $.colorbox.resize();
        }
      }
      // Check if we're not in a test environment before scrolling
      if (!window.fixture && typeof window !== 'undefined') {
        // Only scroll if the data-scroll-to-view attribute is present
        const shouldScroll = $parent.attr('data-scroll-to-view') === 'true' || $el.attr('data-scroll-to-view') === 'true';
        if (shouldScroll) {
          const headerOffset = $parent.offset().top;
          // Check for a custom offset value
          const scrollOffset = parseInt($parent.attr('data-scroll-offset') || $el.attr('data-scroll-offset') || 0, 10);
          $('html, body').animate({
            scrollTop: headerOffset - scrollOffset,
          }, 300);
        }
      }

      onComplete();
    },
  );
  $parent.find(accLabel).attr('aria-expanded', true);
  $parent
    .addClass(accActive)
    .children(accRadio).prop(CHECKED_ATTR, true);
};

/**
 * Closes an existing tab that has directly been interacted with.
 * @private
 * @param  {jQuery} $parent - The parent containing the entire tab.
 * @param  {Object} options - Any options provided upon initialization
 * @param  {string} options.accLabel - Selector representing the label className
 * @param  {string} options.accActive - Selector representing the active
 * className
 * @param  {string} options.accContent - Selector representing the content for
 * the tab
 * @param  {number} options.accSpeed - Speed in ms for the closing animation
 */
const closeExistingActiveTab = ($parent, { accLabel, accActive, accContent, accSpeed }) => {
  $parent.find(accContent).stop().animate(
    { height: 0 },
    accSpeed,
    function callback() {
      $(this).removeAttr('style');
      $parent.removeClass(accActive);
    },
  );
  $parent.find(accLabel).attr('aria-expanded', false);
};

/**
 * Handles the click event when a label has been interacted with.
 * @private
 * @param {jQuery.Event} evt - A jQuery event
 * @param {HTMLElement} currentTarget - The current target interacted with
 * @param {jQuery} $el - Containing the elements for the accordion
 * @param {Object} opts - Options passed along from initialization
 */
const itemClickHandler = ({ currentTarget }, $el, opts) => {
  const { accActive, accItem, accToggle } = opts;
  const $parent = $(currentTarget).closest(accItem);

  opts.onLoad({ target: $el, opts });

  if (!$parent.hasClass(accActive)) {
    if (!accToggle) {
      closeActiveTab($el, opts);
    }

    openTab($el, $parent, opts);
  } else {
    closeExistingActiveTab($parent, opts);
  }
};

/**
 * Utility function to pull query parameter form URL
 * @private
 * @param {String} name - String to look for in URL
 */
const getParameterByName = (name) => {
  const nameReplace = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
  const regex = new RegExp(`[\\?&]${nameReplace}=([^&#]*)`);
  const results = regex.exec(location.search);
  return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
};

/**
 * Applies any sort of event handlers for the accordion.
 * @private
 * @param {jQuery} $el - Containing the elements for the accordion
 * @param {Object} options - Options used for wiring up the accordion
 * @see DEFAULTS
 */
const applyEventHandlers = ($el, $activeLabels, opts = {}) => {
  const { accItem, accQueryOpen } = opts;
  // check if accordion has data attr set to true
  if (accQueryOpen) {
    // check for query string in URL
    const accordionOpen = getParameterByName('accordionOpen');
    if (accordionOpen) {
      // set parent for openTab
      const $parent = $el.find(`#${accordionOpen}`).closest(accItem);
      //  Set animation speed to 0 so default active tab closes as quick as possible.
      closeActiveTab($el, { ...opts, accSpeed: 0 });
      openTab($el, $parent, { ...opts, accSpeed: 0 });
    }
  }

  $activeLabels.on(CLICK_EVENT, evt => itemClickHandler(evt, $el, opts));
};

/**
 * Initializes an accordion based on provided parameters.
 * @param {HTMLElement} el - An element containing the elements for the
 * accordion
 * @param {Object} options - Options used for wiring up the accordion
 * @see DEFAULTS
 */
export default function accordion(el, options = {}) {
  const $el = $(el);
  const opts = mergeOptions($el, options);
  const $activeLabels = $el.find(opts.accItem).not(FORCE_OPEN_CLASS).find(opts.accLabel);

  applyDefaultsToActive($el, opts);
  applyDefaultsToRadio($el, opts);
  applyDefaultsToForce($el, opts);
  applyEventHandlers($el, $activeLabels, opts);

  return {
    closeAll: () => closeActiveTab($el, opts),
  };
}

// Expose the function as a jQuery plugin for ease of use
registerJQueryPlugin(PLUGIN_NAME, accordion);



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/accordion.js